/*!
 * button.js — the button() cue
 * © 2025 Rob Canning — GPLv3
 *
 * Turns an SVG element into a clickable control by laying an absolutely-
 * positioned HTML button over it, and fires a cue (or navigates, or toggles
 * visibility) when it is pressed. The overlay is HTML rather than SVG so it
 * can carry a real label, cursor and hit area; keeping it aligned with the
 * moving/scrolling score is most of the work here (see positionOverlay).
 *
 * One overlay per element: re-registering destroys the previous button first,
 * so leaving and re-entering a page does not leave ghost buttons behind.
 */

import { pulse } from "../control/liveSignals.js";
import { parseCueToAST } from "../parser/parser.js";
import { splitCueId, truthy } from "../parser/cueUtils.js";
import { mayOperate } from "./controlAccess.js";
import { handleCueTrigger } from "./cueDispatcher.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { hideElement, findElementByUid, getSavedVisibility } from "./animShared.js";
import { repositionAllLaunchers } from "../control/o2pLauncher.js";
import { getProjectName } from "../control/controlShared.js";   // positions are kept per project
import { repositionAllHitLabels } from "./animOverlays.js";
import { onReset } from "../system/teardown.js";

// A cue's DSL lives in data-oscilla, or in the id when it was typed straight
// into Inkscape's object properties. Every scan has to look in both, and the
// attribute wins — the bundled scores ship normalized, so an id-only query
// finds nothing in them at all.
const cueDslOf = (el) => (el?.getAttribute?.("data-oscilla") || el?.id || "");
const bothSources = (attrMatch) => `[id${attrMatch}], [data-oscilla${attrMatch}]`;

// ============================================================================
// Position an absolutely-positioned HTML overlay over an SVG element.
// Uses getBoundingClientRect rather than getBBox+getScreenCTM so it accounts
// for CSS transforms on HTML ancestors (e.g. #scrollStage's translateX).
// containerEl must have position: relative (or otherwise be a containing
// block) for the overlay to anchor correctly.
// ============================================================================
function placeOverlayOver(overlay, svgEl, containerEl) {
  const elRect = svgEl.getBoundingClientRect();
  if (!elRect.width && !elRect.height) return;
  const container = containerEl.getBoundingClientRect();

  overlay.style.left = `${elRect.left - container.left}px`;
  overlay.style.top = `${elRect.top - container.top}px`;
  overlay.style.width = `${elRect.width}px`;
  overlay.style.height = `${elRect.height}px`;
}

// ============================================================================
// Toggle State Indicator Styles
// ============================================================================

const TOGGLE_INDICATOR_STYLES = {
  visible: "outline: 2px solid rgba(100, 200, 100, 0.8); outline-offset: 2px;",
  hidden: "outline: 2px dashed rgba(200, 100, 100, 0.6); outline-offset: 2px;"
};

/**
 * Check if a target element (by uid) is currently visible
 */
function isTargetVisible(uid) {
  const found = findElementByUid(uid);
  if (!found?.el) return true; // Default to visible if not found
  const el = found.el;
  return el.style.display !== "none" && el.style.visibility !== "hidden";
}

/**
 * Update the toggle indicator on an overlay based on target visibility
 * @param {HTMLElement} overlay - The overlay element
 * @param {string} uid - Target element uid
 * @param {boolean} [forceState] - Optional: force a specific state (true=visible, false=hidden)
 */
function updateToggleIndicator(overlay, uid, forceState) {
  if (!overlay._isToggleOverlay || !uid) return;
  // indicator:0 — a plain button: no outline saying shown / hidden
  if (overlay._noIndicator) return;
  const isVisible = forceState !== undefined ? forceState : isTargetVisible(uid);
  overlay.style.outline = "";
  overlay.style.outlineOffset = "";
  const style = isVisible ? TOGGLE_INDICATOR_STYLES.visible : TOGGLE_INDICATOR_STYLES.hidden;
  overlay.style.cssText += style;
}

/**
 * Get initial visibility state for a toggle, checking saved state first
 * @param {string} uid - Target element uid
 * @param {object} visibleArg - The visible: argument from AST (may be undefined)
 * @returns {boolean} - Initial visibility state
 */
function getInitialVisibility(uid, visibleArg) {
  // Priority 1: Check saved state from JSON
  const savedState = getSavedVisibility(uid);
  if (savedState !== undefined) {
    return savedState;
  }
  // Priority 2: Use visible: parameter from cue
  if (visibleArg !== undefined) {
    return visibleArg.value !== false;
  }
  // Priority 3: Default to visible
  return true;
}

/**
 * Refresh all toggle indicators based on current element visibility
 * Called when visibility state is loaded from server
 */
export function refreshAllToggleIndicators() {
  document.querySelectorAll(".oscilla-click-overlay, .oscilla-drag-click-overlay").forEach(overlay => {
    if (overlay._isToggleOverlay && overlay._targetUid) {
      updateToggleIndicator(overlay, overlay._targetUid);
    }
  });
}

/**
 * Refresh toggle indicators for a specific uid
 * Called when a toggle happens to sync all buttons targeting the same element
 */
export function refreshToggleIndicatorsForUid(uid) {
  if (!uid) return;
  document.querySelectorAll(".oscilla-click-overlay, .oscilla-drag-click-overlay").forEach(overlay => {
    if (overlay._isToggleOverlay && overlay._targetUid === uid) {
      updateToggleIndicator(overlay, uid);
    }
  });
}

// Listen for visibility state loaded event to refresh indicators
window.addEventListener("oscilla:visibility-state-loaded", () => {
  // Small delay to let visibility changes apply
  requestAnimationFrame(() => {
    refreshAllToggleIndicators();
  });
});

// ============================================================================
// Drag Position Persistence
// ============================================================================

const DRAG_POSITIONS_KEY = "oscilla.dragPositions";

/**
 * Extract uid from element id like "drag(1, uid:inputLevels, drag-html:1)"
 */
function extractUidFromId(id) {
  if (!id) return null;
  const match = id.match(/uid:\s*([^,\s)]+)/);
  return match ? match[1] : null;
}

/**
 * Load all saved drag positions for current project
 */
function loadDragPositions() {
  try {
    const stored = localStorage.getItem(DRAG_POSITIONS_KEY);
    if (!stored) return {};
    const all = JSON.parse(stored);
    return all[getProjectName()] || {};
  } catch (e) {
    console.warn("[dragPositions] Failed to load:", e);
    return {};
  }
}

/**
 * Save a drag position for a uid
 */
function saveDragPosition(uid, x, y) {
  try {
    const stored = localStorage.getItem(DRAG_POSITIONS_KEY);
    const all = stored ? JSON.parse(stored) : {};
    const project = getProjectName();
    if (!all[project]) all[project] = {};
    all[project][uid] = { x, y };
    localStorage.setItem(DRAG_POSITIONS_KEY, JSON.stringify(all));
  } catch (e) {
    console.warn("[dragPositions] Failed to save:", e);
  }
}

/**
 * Get saved position for a uid, or null if none
 */
function getSavedDragPosition(uid) {
  const positions = loadDragPositions();
  return positions[uid] || null;
}

export function destroyAllCueButtons() {
  document.querySelectorAll(".oscilla-cue-button").forEach(btn => {
    try {
      btn._destroyCueButton?.();   // its resize listener, its marker's pointer to it
    } catch (e) {
      console.warn("[cueButtons] destroy failed:", e);
    }

    btn.remove();                  // ✅ ensure DOM cleared
  });

}

export function buildCueButtonsIn(svgRoot, containerEl) {
  if (!svgRoot) return [];

  // ✔ Select correct container for PAGE MODE
  if (!containerEl) {
    containerEl =
      document.getElementById("singlePage-content") ||
      document.getElementById("pageOverlay") ||
      document.getElementById("scoreInner");
  }

  if (containerEl instanceof SVGElement) {
    console.warn("[cueButton] ❌ Container was SVG — overriding to singlePage-content");
    containerEl = document.getElementById("singlePage-content");
  }

  if (!containerEl) {
    console.warn("[cueButtons] ⚠ No containerEl found for button placement.");
    return [];
  }

  const built = [];
  // A button's DSL lives in data-oscilla, or in the id when it was typed
  // straight into Inkscape. Both have to be found, and the attribute wins —
  // the bundled scores ship normalized, so an id-only query finds nothing in
  // them at all.
  const elements = svgRoot.querySelectorAll('[id^="button("], [data-oscilla^="button("]');

  elements.forEach(el => {
    const cueExpr = (el.getAttribute("data-oscilla") || el.id).trim();

    let ast;
    try {
      ast = parseCueToAST(cueExpr);
    } catch (e) {
      console.warn("[cueButton] ❌ parse failed:", cueExpr, e);
      return;
    }

    if (!ast || ast.type !== "cueButton") return;

    const triggerAst = ast.triggerAst || ast.trigger;

    const label =
      (ast.label && ast.label.trim()) ||
      (triggerAst.uid && String(triggerAst.uid)) ||
      (triggerAst.action && String(triggerAst.action)) ||
      (triggerAst.page && String(triggerAst.page)) ||
      "button";

    const btn = createCueButtonForElement(el, {
      triggerAst,
      label,
      opt: { ...ast.opt, containerEl }
    });

    if (btn) built.push(btn);
  });

  return built;
}

export function createCueButtonForElement(
  cueSvgEl,
  { triggerAst, label = "", opt = {} }
) {
  if (!triggerAst) {
    return null;
  }

  // ------------------------------------------------------------
  // 1. Resolve container
  // ------------------------------------------------------------
  let containerEl =
    opt.containerEl ||
    document.getElementById("singlePage-content") ||
    document.getElementById("pageOverlay") ||
    document.getElementById("scoreInner");

  if (containerEl instanceof SVGElement) {
    containerEl = document.getElementById("singlePage-content");
  }

  if (!containerEl) {
    return null;
  }

  // ------------------------------------------------------------
  // 2. Check for overlay mode - SVG stays visible, invisible click overlay
  // ------------------------------------------------------------
  const isOverlayMode = opt.mode === "overlay" || opt.style?.mode === "overlay";

  if (isOverlayMode) {
    return createOverlayButton(cueSvgEl, triggerAst, containerEl, opt);
  }

  // ------------------------------------------------------------
  // 3. Standard mode: Hide SVG marker unless part of reuse clone
  // ------------------------------------------------------------
  const insideReuse = !!cueSvgEl.closest('g[id^="reuse("]');

  if (!insideReuse) {
    cueSvgEl.style.visibility = "hidden";
  }

  // ------------------------------------------------------------
  // 4. Create HTML button
  // ------------------------------------------------------------
  // One overlay per element. assignCues rebuilds buttons on EVERY run — a page
  // swap, a rescan, returning to the score — and nothing destroyed the last
  // set, so each pass appended another full copy on top of the previous one.
  // Leaving a page left its menu stacked over the score: ghost buttons that
  // looked real and answered clicks.
  const _previous = cueSvgEl._oscillaCueButton;
  if (_previous) {
    // Hold the reference first: _destroyCueButton clears cueSvgEl's pointer,
    // so reading it again here would be null and the remove() would throw
    // into the catch — leaving the old button on screen, which is the very
    // thing this guard exists to prevent.
    try { _previous._destroyCueButton?.(); } catch { /* already torn down */ }
    try { _previous.remove(); } catch { /* already gone with its container */ }
    cueSvgEl._oscillaCueButton = null;
  }

  const btn = document.createElement("button");
  btn.type = "button";
  btn.textContent = label;
  btn.className = "oscilla-cue-button";
  btn.style.position = "absolute";

  containerEl.appendChild(btn);
  cueSvgEl._oscillaCueButton = btn;

  // ------------------------------------------------------------
  // 4. Normalize style block (opt.key → opt.style.key)
  // ------------------------------------------------------------
  if (!opt.style) {
    const styleKeys = [
      "size",
      "label",
      "font",
      "fontsize",
      "color",
      "textcolor",
      "active",
      "uid"
    ];
    const styleObj = {};
    let found = false;

    for (const k of styleKeys) {
      if (opt[k] !== undefined) {
        styleObj[k] = opt[k];
        found = true;
      }
    }

    if (found) {
      opt.style = styleObj;
    }
  }

  // ------------------------------------------------------------
  // 5. Apply style() block
  // ------------------------------------------------------------
  if (opt.style) {
    const s = opt.style;
    // Label override
    if (s.label) btn.textContent = s.label;

    // Size: "120x45", in SCORE UNITS like every other measurement in the DSL,
    // so the button scales with the score as the marker it sits on does.
    // place() below rescales it.
    if (typeof s.size === "string") {
      const [w, h] = s.size.split("x").map(Number);
      if (!isNaN(w) && !isNaN(h)) btn._oscillaSize = { w, h };
      // An initial value so the button is not mis-sized for the frames before
      // the first placement runs.
      if (!isNaN(w)) btn.style.width = `${w}px`;
      if (!isNaN(h)) btn.style.height = `${h}px`;
    }

    // Background color
    if (s.color) {
      btn.style.backgroundColor = s.color;
    }

    // Text color
    if (s.textcolor) {
      btn.style.color = s.textcolor;
    }

    // Font family
    if (s.font) {
      btn.style.fontFamily = s.font;
    }

    // Font size
    if (s.fontsize) {
      // Also score units — a label that kept its pixel size inside a button
      // that scales would overflow it at one scale and swim in it at another.
      btn._oscillaFontSize = Number(s.fontsize);
      btn.style.fontSize = `${s.fontsize}px`;
    }

    // Active effect: flash
    if (s.active === "flash") {
      btn.classList.add("oscilla-button-flashable");
    }
  }

  // ------------------------------------------------------------
  // 7. Placement using BCR
  // ------------------------------------------------------------
  const place = () => {
    // Use getBoundingClientRect rather than getBBox+getScreenCTM:
    // getScreenCTM ignores CSS transforms on HTML ancestors (e.g. #scrollStage's
    // translateX scrolling), so its coords drift relative to actual rendering.
    const elRect = cueSvgEl.getBoundingClientRect();
    const container = containerEl.getBoundingClientRect();

    btn.style.left = `${elRect.left - container.left + (opt.offsetX || 0)}px`;
    btn.style.top = `${elRect.top - container.top + (opt.offsetY || 0)}px`;

    // Scale a declared size: by however much this host is drawing the marker,
    // measured off the same rect used for the position rather than a CTM, so
    // the two cannot disagree. Re-read every placement, so zoom, resize and a
    // move between score and page all stay right.
    if (btn._oscillaSize) {
      let bw = 0;
      try { bw = cueSvgEl.getBBox().width; } catch { /* not measurable yet */ }
      const scale = bw > 0 ? elRect.width / bw : 1;
      btn.style.width = `${btn._oscillaSize.w * scale}px`;
      btn.style.height = `${btn._oscillaSize.h * scale}px`;
      if (btn._oscillaFontSize) btn.style.fontSize = `${btn._oscillaFontSize * scale}px`;
    }
  };

  requestAnimationFrame(() =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        place();
      }
      )
    )
  );

  window.addEventListener("resize", place);

  // ------------------------------------------------------------
  // 8. Button click → trigger cue
  // ------------------------------------------------------------
  btn.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (opt.style?.active === "flash") {
      btn.classList.add("flash");
      setTimeout(() => btn.classList.remove("flash"), 150);
    }

    // <id>.hit / .n on the signal bus: a press, for follow()
    if (cueSvgEl?.id) pulse(cueSvgEl.id, "button");

    // a click, as on the cue's own element: a synth it plays stops on the next one
    cueSvgEl._clickTriggered = true;
    try {
      handleCueTrigger(triggerAst, false, true, cueSvgEl);
    } catch (err) {
      console.error("[cueButton] ❌ Error in handleCueTrigger:", err);
    } finally {
      cueSvgEl._clickTriggered = false;
    }
  });

  btn._cueMarkerEl = cueSvgEl;
  btn._place = place;

  // Its resize listener goes with it, and a later rebuild for this element
  // knows there is nothing left to clear.
  btn._destroyCueButton = () => {
    window.removeEventListener("resize", place);
    if (cueSvgEl._oscillaCueButton === btn) cueSvgEl._oscillaCueButton = null;
  };

  return btn;
}

// ============================================================================
// OVERLAY BUTTON - invisible click overlay over SVG element
// Used when mode:overlay is specified in button() style block
// SVG element stays visible, transparent overlay captures clicks
// ============================================================================

function createOverlayButton(cueSvgEl, triggerAst, containerEl, opt) {
  // Skip if already has overlay
  if (cueSvgEl._buttonOverlayBound) return null;
  cueSvgEl._buttonOverlayBound = true;

  // Mark SVG element as overlay mode - prevents hideAllButtonPlaceholders from hiding it
  cueSvgEl.dataset.buttonMode = "overlay";

  // Create invisible overlay div
  const overlay = document.createElement("div");
  overlay.className = "oscilla-button-overlay oscilla-cue-button";
  overlay.style.cssText = `
    position: absolute;
    background: transparent;
    cursor: pointer;
    z-index: 100015;
  `;

  // Add tooltip from style block if specified
  if (opt.style?.tooltip || opt.tooltip) {
    overlay.title = opt.style?.tooltip || opt.tooltip;
  }

  containerEl.appendChild(overlay);

  // Position overlay over SVG element
  const place = () => placeOverlayOver(overlay, cueSvgEl, containerEl);

  // Initial placement with delays for transform application
  setTimeout(() => {
    place();
    setTimeout(place, 200);
  }, 50);

  window.addEventListener("resize", place);
  overlay._place = place;

  // Prevent pointer events from reaching score
  overlay.addEventListener("pointerdown", (e) => {
    e.stopPropagation();
  });
  overlay.addEventListener("touchstart", (e) => {
    e.stopPropagation();
  }, { passive: true });

  // Click handler
  overlay.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Flash effect on the SVG element
    const activeStyle = opt.style?.active || opt.active;
    if (activeStyle === "flash") {
      cueSvgEl.style.filter = "brightness(1.3)";
      setTimeout(() => { cueSvgEl.style.filter = ""; }, 150);
    }

    cueSvgEl._clickTriggered = true;   // a click (see above)
    try {
      handleCueTrigger(triggerAst, false, true, cueSvgEl);
    } catch (err) {
      console.error("[cueButton:overlay] Error:", err);
    } finally {
      cueSvgEl._clickTriggered = false;
    }
  });

  // Visual feedback on pointer down
  overlay.addEventListener("pointerdown", () => {
    cueSvgEl.style.filter = "brightness(0.85)";
  });
  overlay.addEventListener("pointerup", () => {
    cueSvgEl.style.filter = "";
  });
  overlay.addEventListener("pointerleave", () => {
    cueSvgEl.style.filter = "";
  });

  overlay._svgElement = cueSvgEl;
  overlay._cueMarkerEl = cueSvgEl;

  return overlay;
}

// ============================================================================
// ONE ELEMENT, LIVE. The editor has just written new DSL onto an element and
// nothing is reloading. Every builder above walks a root and skips whatever is
// already bound, and they run at load — so without this a button added in the
// editor would not be clickable until the next reload.
//
// Tear this element's overlays down, clear its bound flags, and run the
// builders over a root that contains only it. The container follows the
// element: the page it sits on, else the scrolling score.
// ============================================================================
const OVERLAY_SELECTOR = ".oscilla-cue-button, .oscilla-click-overlay, .oscilla-drag-click-overlay, .oscilla-drag-overlay";

/** A torn-down click overlay leaves its radio: and group: (preset) groups. */
function leaveButtonGroups(ov) {
  const drop = (registry, grp) => {
    const list = registry?.[grp];
    if (!list) return;
    const i = list.indexOf(ov);
    if (i >= 0) list.splice(i, 1);
    if (!list.length) delete registry[grp];
  };
  if (ov._radioGroup != null) drop(window._clickRadioGroups, ov._radioGroup);
  if (ov._presetGroup != null) drop(window._buttonPresetGroups, ov._presetGroup);
}

/** A project switch: every button's and overlay's groups are the old score's. */
export function resetButtonGroups() {
  window._clickRadioGroups = {};
  window._buttonPresetGroups = {};
}
// the cue buttons, with their resize listeners; radio: and group: (preset) groups
onReset("project", destroyAllCueButtons);
onReset("project", resetButtonGroups);

export function destroyOverlaysFor(el) {
  if (!el) return;
  document.querySelectorAll(OVERLAY_SELECTOR).forEach(ov => {
    if (ov._svgElement !== el && ov._cueMarkerEl !== el) return;
    try { ov._destroyCueButton?.(); } catch { /* already torn down */ }
    if (ov._place) window.removeEventListener("resize", ov._place);
    leaveButtonGroups(ov);
    ov.remove();
  });
  el._buttonOverlayBound = false;
  el._clickOverlayBound = false;
  el._dragClickOverlayBound = false;
  el._dragOverlayBound = false;
  el._oscillaCueButton = null;
  if (el.dataset) delete el.dataset.buttonMode;
  // the html-mode button hides its marker; a DSL that no longer wants one
  // must get the shape back
  if (el.style) { el.style.visibility = ""; el.style.filter = ""; }
  if (Array.isArray(window._activePageButtons)) {
    window._activePageButtons = window._activePageButtons.filter(b => b.isConnected);
  }
}

export function rebuildOverlaysFor(el) {
  if (!el?.isConnected) return [];
  destroyOverlaysFor(el);
  const page = el.closest?.("[data-oscilla-page-file]");
  const containerEl = page || document.getElementById("scoreInner");
  if (!containerEl) return [];
  // A root the builders can query that holds this one element.
  const root = { querySelectorAll: (sel) => (el.matches?.(sel) ? [el] : []) };
  const built = [
    ...buildCueButtonsIn(root, containerEl),
    ...buildClickOverlaysIn(root, containerEl),
    ...buildDragClickOverlaysIn(root, containerEl),
    ...buildDragOverlaysIn(root, containerEl),
  ];
  if (page && Array.isArray(window._activePageButtons)) {
    window._activePageButtons.push(...built.filter(b => b.classList?.contains("oscilla-cue-button")));
  }
  return built;
}

/**
 * Reposition all button overlays (mode:overlay buttons)
 * Call during/after dragging a parent group
 */
export function repositionAllButtonOverlays() {
  document.querySelectorAll(".oscilla-button-overlay").forEach(overlay => {
    if (overlay._place) {
      overlay._place();
    }
  });
}


export function hideAllButtonPlaceholders(svgRoot) {
  const markers = svgRoot.querySelectorAll('[id^="button("], [data-oscilla^="button("]');
  markers.forEach(el => {
    // Skip overlay mode buttons - they should remain visible
    if (el.dataset.buttonMode === "overlay") return;
    el.style.opacity = "0";
    el.style.pointerEvents = "none";
  });
}


// ============================================================================
// INVISIBLE CLICK OVERLAYS for click:1 parameter
// Creates transparent HTML overlays that capture clicks for SVG elements
// SVG element remains visible - overlay just handles click events
// ============================================================================

export function buildClickOverlaysIn(svgRoot, containerEl) {
  if (!svgRoot) return [];

  if (!containerEl) {
    containerEl =
      document.getElementById("singlePage-content") ||
      document.getElementById("pageOverlay") ||
      document.getElementById("scoreInner");
  }

  if (containerEl instanceof SVGElement) {
    containerEl = document.getElementById("singlePage-content");
  }

  if (!containerEl) {
    console.warn("[clickOverlay] No container found");
    return [];
  }

  const built = [];

  // Find all elements that have click:1 in their cue id
  // We need to parse to check for click:1 parameter
  const allCueElements = svgRoot.querySelectorAll(
    bothSources('*="click:1"') + ', ' + bothSources('*="click: 1"'));

  const clicks = (ast) => {
    const arg = ast?.args?.find(a => a.type === "click");
    return !!arg && truthy(arg.value);
  };

  allCueElements.forEach(el => {
    // An element may carry several cues ("ui(…, click:1) pin(…)"): the one
    // that clicks is the one the overlay runs. Parsed whole, a second cue
    // would make the parse fail and the element get no overlay at all.
    let ast = null;
    for (const expr of splitCueId(cueDslOf(el).trim())) {
      let a;
      try { a = parseCueToAST(expr); } catch { continue; }
      if (clicks(a)) { ast = a; break; }
    }
    if (!ast) return;

    // Skip if already has overlay
    if (el._clickOverlayBound) return;
    el._clickOverlayBound = true;

    // Check for initial visibility state (visible:false means start hidden)
    const visibleArg = ast.args?.find(a => a.type === "visible");
    const uidArg = ast.args?.find(a => a.type === "uid");
    const actionArg = ast.args?.find(a => a.type === "action");
    const isToggleAction = uidArg && (actionArg?.value === "toggle" || (!actionArg && visibleArg));

    if (visibleArg && visibleArg.value === false && uidArg) {
      // Apply initial hidden state with delay to let o2p touch handlers initialize first
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            hideElement(uidArg.value, { dur: 0 });
          });
        });
      }, 100);  // 100ms delay ensures o2p handlers are fully initialized
    }

    // Create invisible overlay div
    const overlay = document.createElement("div");
    overlay.className = "oscilla-click-overlay";
    overlay.style.cssText = `
      position: absolute;
      background: transparent;
      cursor: pointer;
      z-index: 100015;
      border-radius: 4px;
    `;

    // Mark as toggle overlay and set initial indicator
    if (isToggleAction && uidArg) {
      overlay._isToggleOverlay = true;
      overlay._noIndicator = [0, "0", false, "false"].includes(ast.args?.find(a => a.type === "indicator")?.value);
      overlay._targetUid = uidArg.value;
      overlay.dataset.targetUid = uidArg.value;
      // Determine initial visibility: saved state > visible: param > default true
      const initialVisible = getInitialVisibility(uidArg.value, visibleArg);
      updateToggleIndicator(overlay, uidArg.value, initialVisible);
    }

    // Add tooltip if specified (tooltip:xxx parameter)
    const tooltipArg = ast.args?.find(a => a.type === "tooltip");
    if (tooltipArg && tooltipArg.value) {
      overlay.title = tooltipArg.value;
    } else if (uidArg) {
      // Auto-generate tooltip from action and uid
      const action = actionArg?.value || "toggle";
      overlay.title = `${action}: ${uidArg.value}`;
    }

    containerEl.appendChild(overlay);

    // Position overlay over SVG element
    const place = () => placeOverlayOver(overlay, el, containerEl);

    // Initial placement - use setTimeout to ensure transforms are applied
    // Also reposition after a longer delay as safety
    setTimeout(() => {
      place();
      setTimeout(place, 200);
    }, 50);

    window.addEventListener("resize", place);

    // Store place function for repositioning after drag
    overlay._place = place;

    // Prevent all pointer events from reaching the score
    overlay.addEventListener("pointerdown", (e) => {
      e.stopPropagation();
    });
    overlay.addEventListener("touchstart", (e) => {
      e.stopPropagation();
    }, { passive: true });

    // Detect toggle:1 param — client-side binary toggle with green outline feedback
    const isClientToggle = ast.args?.find(a => a.type === "toggle" && truthy(a.value));
    if (isClientToggle) {
      overlay._toggleOn = false;
    }

    // Detect labels:'OFF_TEXT,ON_TEXT' param — two text states for the sibling SVG text element
    // e.g. labels:'PAUSE,PLAY' shows "PAUSE" when playing (off), "PLAY" when paused (on)
    const labelsArg = ast.args?.find(a => a.type === "labels");
    if (labelsArg?.value && isClientToggle) {
      const parts = String(labelsArg.value).split(',');
      overlay._labelOff = parts[0]?.trim() || '';
      overlay._labelOn  = parts[1]?.trim() || '';
      // Find the associated SVG text element — search siblings, then fall back to
      // proximity search within the parent group (handles Inkscape ordering quirks)
      const btnX = parseFloat(el.getAttribute('x')) || 0;
      const btnW = parseFloat(el.getAttribute('width')) || 90;
      const btnCx = btnX + btnW / 2;
      let found = null;
      // First pass: nearest text sibling
      let sib = el.nextElementSibling;
      for (let i = 0; i < 5 && sib; i++) {
        if (sib.tagName.toLowerCase() === 'text') { found = sib; break; }
        sib = sib.nextElementSibling;
      }
      // Second pass: closest text by x position within parent group
      if (!found && el.parentElement) {
        let bestDist = Infinity;
        for (const t of el.parentElement.querySelectorAll('text')) {
          const tx = parseFloat(t.getAttribute('x')) || 0;
          const dist = Math.abs(tx - btnCx);
          if (dist < bestDist && dist < btnW) { bestDist = dist; found = t; }
        }
      }
      overlay._labelEl = found;
      // Start in the "on" state (showing on-label e.g. "PLAY") so the button
      // reflects "nothing active yet — click to start" rather than "PAUSE".
      overlay._toggleOn = true;
      overlay.style.outline = "4px solid #44dd66";
      overlay.style.outlineOffset = "-2px";
      if (found) found.textContent = overlay._labelOn;
    }

    // Detect radio:groupName param — exclusive selection within named group
    const radioArg = ast.args?.find(a => a.type === "radio");
    if (radioArg?.value) {
      const grp = radioArg.value;
      if (!window._clickRadioGroups) window._clickRadioGroups = {};
      if (!window._clickRadioGroups[grp]) window._clickRadioGroups[grp] = [];
      window._clickRadioGroups[grp].push(overlay);
      overlay._radioGroup = grp;
      overlay._toggleOn = false;
    }

    // Detect group:name param — register button with o2p preset group for state capture
    const groupArg = ast.args?.find(a => a.type === "group");
    if (groupArg?.value) {
      const grp = String(groupArg.value);
      overlay._presetGroup = grp;
      overlay._oscAddr = ast.args?.find(a => a.type === "addr")?.value || el.id;
      if (!window._buttonPresetGroups) window._buttonPresetGroups = {};
      if (!window._buttonPresetGroups[grp]) window._buttonPresetGroups[grp] = [];
      window._buttonPresetGroups[grp].push(overlay);
    }

    // Click handler - skip if drag is in progress
    overlay.addEventListener("click", (e) => {
      // Check if any drag is currently active
      if (window._dragRegistry) {
        for (const [, entry] of window._dragRegistry) {
          if (entry.dragging) {
            return;
          }
        }
      }

      e.preventDefault();
      e.stopPropagation();
      if (!mayOperate(ast)) return;          // allow:/lock: (controlAccess.js)

      try {
        // For toggle:1 buttons: flip visual state FIRST, then send explicit 0/1
        // so SC receives the new state and can SET rather than guess-toggle.
        // For all other buttons: fire the normal cue trigger (sends visual geometry).
        if (isClientToggle) {
          overlay._toggleOn = !overlay._toggleOn;
          overlay.style.outline = overlay._toggleOn
            ? "4px solid #44dd66"
            : "";
          overlay.style.outlineOffset = "-2px";

          // Swap label text if two-state labels are configured
          if (overlay._labelEl) {
            overlay._labelEl.textContent = overlay._toggleOn
              ? overlay._labelOn
              : overlay._labelOff;
          }

          // Send explicit state value (0 or 1) — SC should SET not toggle
          const addr = ast.args?.find(a => a.type === "addr")?.value;
          if (addr) {
            sendOSC({
              type: "osc_value",
              addr,
              args: [overlay._toggleOn ? 1 : 0],
              timestamp: Date.now()
            }, { immediate: true });
          }
        } else {
          handleCueTrigger(ast, false, true, el);
        }

        // Radio group: activate this, deactivate all others in same group
        if (overlay._radioGroup) {
          const grp = window._clickRadioGroups?.[overlay._radioGroup] || [];
          grp.forEach(o => {
            o._toggleOn = (o === overlay);
            o.style.outline = o._toggleOn ? "4px solid #44dd66" : "";
            o.style.outlineOffset = "-2px";
          });
          // Radio buttons still use handleCueTrigger (each fires its own distinct OSC addr)
          handleCueTrigger(ast, false, true, el);
        }

        // Update ALL toggle indicators for this uid (sync multiple buttons)
        if (overlay._isToggleOverlay && overlay._targetUid) {
          requestAnimationFrame(() => {
            refreshToggleIndicatorsForUid(overlay._targetUid);
          });
        }
      } catch (err) {
        console.error("[click:1] Error:", err);
      }
    });

    // Visual feedback on the SVG element
    overlay.addEventListener("pointerdown", () => {
      el.style.filter = "brightness(0.85)";
    });
    overlay.addEventListener("pointerup", () => {
      el.style.filter = "";
    });
    overlay.addEventListener("pointerleave", () => {
      el.style.filter = "";
    });

    overlay._svgElement = el;
    built.push(overlay);
  });

  return built;
}

export function destroyAllClickOverlays() {
  document.querySelectorAll(".oscilla-click-overlay").forEach(el => {
    if (el._svgElement) {
      el._svgElement._clickOverlayBound = false;
    }
    el.remove();
  });
}

/**
 * Reposition all click overlays - call after drag or resize
 */
export function repositionAllClickOverlays() {
  document.querySelectorAll(".oscilla-click-overlay").forEach(overlay => {
    if (overlay._place) {
      overlay._place();
    }
  });
}

// ============================================================================
// DRAG-CLICK OVERLAYS for drag-click:1 parameter
// Creates HTML overlays that handle BOTH drag and click on the same element
// - Click triggers the cue action (e.g., toggle visibility)
// - Drag moves the underlying SVG element
// ============================================================================

export function buildDragClickOverlaysIn(svgRoot, containerEl) {
  if (!svgRoot) return [];

  if (!containerEl) {
    containerEl =
      document.getElementById("singlePage-content") ||
      document.getElementById("pageOverlay") ||
      document.getElementById("scoreInner");
  }

  if (containerEl instanceof SVGElement) {
    containerEl = document.getElementById("singlePage-content");
  }

  if (!containerEl) {
    console.warn("[dragClickOverlay] No container found");
    return [];
  }

  const built = [];

  // Find elements with drag-click:1 parameter
  const allCueElements = svgRoot.querySelectorAll(
    bothSources('*="drag-click:1"') + ', ' + bothSources('*="drag-click: 1"'));

  const dragClicks = (ast) => {
    const arg = ast?.args?.find(a => a.type === "drag-click");
    return !!arg && truthy(arg.value);
  };

  allCueElements.forEach(el => {
    // the cue that drag-clicks, of however many the element carries (see
    // buildClickOverlaysIn)
    const cueExpr = cueDslOf(el).trim();
    let ast = null;
    for (const expr of splitCueId(cueExpr)) {
      let a;
      try { a = parseCueToAST(expr); } catch (e) { console.warn("[dragClickOverlay] Parse failed:", expr, e); continue; }
      if (dragClicks(a)) { ast = a; break; }
    }
    if (!ast) return;

    // Skip if already bound
    if (el._dragClickOverlayBound) return;
    el._dragClickOverlayBound = true;

    const uidArg = ast.args?.find(a => a.type === "uid");
    const visibleArg = ast.args?.find(a => a.type === "visible");

    // Apply initial hidden state if visible:false
    // Use extra delay to let o2p touch handlers initialize first (they also use 2x RAF)
    if (visibleArg && visibleArg.value === false && uidArg) {
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            hideElement(uidArg.value, { dur: 0 });
          });
        });
      }, 100);  // 100ms delay ensures o2p handlers are fully initialized
    }

    // Check if this is a toggle action
    const actionArg = ast.args?.find(a => a.type === "action");
    const isToggleAction = uidArg && (actionArg?.value === "toggle" || (!actionArg && visibleArg));

    // Restore saved position for this toggle icon
    // Store initial transform to detect invalid saved positions
    const toggleUid = uidArg?.value ? `toggle_${uidArg.value}` : null;
    const initialTransform = el.getAttribute("transform") || "";
    const initialMatch = initialTransform.match(/translate\(\s*([-\d.]+)[,\s]+([-\d.]+)\s*\)/);
    const initialX = initialMatch ? parseFloat(initialMatch[1]) : 0;
    const initialY = initialMatch ? parseFloat(initialMatch[2]) : 0;

    if (toggleUid) {
      const savedPos = getSavedDragPosition(toggleUid);
      if (savedPos) {
        // Sanity check: saved position should be within reasonable range of initial
        // Allow up to 2000 SVG units of movement from original position
        const maxDrift = 2000;
        const dx = Math.abs(savedPos.x - initialX);
        const dy = Math.abs(savedPos.y - initialY);

        if (dx < maxDrift && dy < maxDrift) {
          el.setAttribute("transform", `translate(${savedPos.x}, ${savedPos.y})`);
        } else {
          console.warn(`[dragClickOverlay] Ignoring invalid saved position for ${toggleUid}: (${savedPos.x}, ${savedPos.y}) too far from initial (${initialX}, ${initialY})`);
          // Clear the bad position
          saveDragPosition(toggleUid, initialX, initialY);
        }
      }
    }

    // Create overlay div
    const overlay = document.createElement("div");
    overlay.className = "oscilla-drag-click-overlay";
    overlay.style.cssText = `
      position: absolute;
      background: transparent;
      cursor: grab;
      z-index: 100010;
      touch-action: none;
      border-radius: 4px;
    `;

    // Mark as toggle overlay and set initial indicator
    if (isToggleAction && uidArg) {
      overlay._isToggleOverlay = true;
      overlay._noIndicator = [0, "0", false, "false"].includes(ast.args?.find(a => a.type === "indicator")?.value);
      overlay._targetUid = uidArg.value;
      overlay.dataset.targetUid = uidArg.value;
      // Determine initial visibility: saved state > visible: param > default true
      const initialVisible = getInitialVisibility(uidArg.value, visibleArg);
      updateToggleIndicator(overlay, uidArg.value, initialVisible);
    }

    // Tooltip
    if (uidArg) {
      const action = actionArg?.value || "toggle";
      overlay.title = `Drag to move, click to ${action}: ${uidArg.value}`;
    }

    containerEl.appendChild(overlay);

    // Track drag state
    let isDragging = false;
    let startX = 0, startY = 0;
    let elStartX = 0, elStartY = 0;
    const DRAG_THRESHOLD = 5;

    // Get current translate from element
    const getTranslate = () => {
      const transform = el.getAttribute("transform") || "";
      const match = transform.match(/translate\(\s*([-\d.]+)[,\s]+([-\d.]+)\s*\)/);
      if (match) {
        return { x: parseFloat(match[1]), y: parseFloat(match[2]) };
      }
      return { x: 0, y: 0 };
    };

    // Set translate on element
    const setTranslate = (x, y) => {
      el.setAttribute("transform", `translate(${x}, ${y})`);
    };

    // Position overlay over SVG element
    const place = () => placeOverlayOver(overlay, el, containerEl);

    // Initial placement - use setTimeout to ensure transforms are applied
    setTimeout(() => {
      place();
      setTimeout(place, 200);
    }, 50);

    window.addEventListener("resize", place);
    overlay._place = place;

    // Pointer handlers
    const onPointerDown = (e) => {
      e.preventDefault();
      e.stopPropagation();

      isDragging = false;
      startX = e.clientX;
      startY = e.clientY;

      const current = getTranslate();
      elStartX = current.x;
      elStartY = current.y;

      overlay.style.cursor = "grabbing";
      try { overlay.setPointerCapture(e.pointerId); } catch { /* pointer already released */ }

      overlay.addEventListener("pointermove", onPointerMove);
      overlay.addEventListener("pointerup", onPointerUp);
      overlay.addEventListener("pointercancel", onPointerUp);
    };

    const onPointerMove = (e) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!isDragging && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        isDragging = true;
      }

      if (isDragging) {
        // Get SVG scale factor from PARENT's CTM (not element's, which includes its own transform)
        const parent = el.parentElement;
        const ctm = parent?.getScreenCTM?.() || el.ownerSVGElement?.getScreenCTM?.();
        const scaleX = ctm ? ctm.a : 1;
        const scaleY = ctm ? ctm.d : 1;

        // Convert screen delta to SVG coordinates
        const svgDx = dx / scaleX;
        const svgDy = dy / scaleY;

        setTranslate(elStartX + svgDx, elStartY + svgDy);
        place(); // Update overlay position
        repositionAllHitLabels(); // Update hit labels during panel drag
      }
    };

    const onPointerUp = (e) => {
      overlay.style.cursor = "grab";
      try { overlay.releasePointerCapture(e.pointerId); } catch { /* capture already released */ }

      overlay.removeEventListener("pointermove", onPointerMove);
      overlay.removeEventListener("pointerup", onPointerUp);
      overlay.removeEventListener("pointercancel", onPointerUp);

      if (!isDragging) {
        // It was a click, trigger the cue
        try {
          handleCueTrigger(ast, false, true, el);

          // Update ALL toggle indicators for this uid (sync multiple buttons)
          if (overlay._isToggleOverlay && overlay._targetUid) {
            requestAnimationFrame(() => {
              refreshToggleIndicatorsForUid(overlay._targetUid);
            });
          }
        } catch (err) {
          console.error("[dragClickOverlay] Error:", err);
        }
      } else {
        // Save drag position
        const current = getTranslate();
        // Save position for toggle icon
        if (toggleUid) {
          saveDragPosition(toggleUid, current.x, current.y);
        }
      }

      isDragging = false;
    };

    overlay.addEventListener("pointerdown", onPointerDown);

    overlay._svgElement = el;
    built.push(overlay);
  });

  return built;
}

export function destroyAllDragClickOverlays() {
  document.querySelectorAll(".oscilla-drag-click-overlay").forEach(el => {
    if (el._svgElement) {
      el._svgElement._dragClickOverlayBound = false;
    }
    el.remove();
  });
}

export function repositionAllDragClickOverlays() {
  document.querySelectorAll(".oscilla-drag-click-overlay").forEach(overlay => {
    if (overlay._place) {
      overlay._place();
    }
  });
}

// ============================================================================
// DRAG-ONLY HTML OVERLAYS for drag-html:1 parameter
// Creates HTML overlays that handle ONLY drag (no click action)
// Use this for panels/controls that need to be draggable without triggering score scroll
// ============================================================================

export function buildDragOverlaysIn(svgRoot, containerEl) {
  if (!svgRoot) return [];

  if (!containerEl) {
    containerEl =
      document.getElementById("singlePage-content") ||
      document.getElementById("pageOverlay") ||
      document.getElementById("scoreInner");
  }

  if (containerEl instanceof SVGElement) {
    containerEl = document.getElementById("singlePage-content");
  }

  if (!containerEl) {
    console.warn("[dragOverlay] No container found");
    return [];
  }

  const built = [];

  // Find elements with drag-html:1 - either in ID or as data attribute (set by preProcessDrag)
  const allDragElements = svgRoot.querySelectorAll(
    bothSources('*="drag-html:1"') + ', ' + bothSources('*="drag-html: 1"') + ', [data-drag-html="1"]');

  allDragElements.forEach(el => {
    // Skip if already bound
    if (el._dragOverlayBound) return;
    el._dragOverlayBound = true;

    // Extract uid for position persistence
    // Check multiple sources: ID parsing, data-oscilla-uid (from preProcessDrag), or data-uid
    const uid = extractUidFromId(el.id) || el.dataset.oscillaUid || el.dataset.uid;

    // Restore saved position if available.
    // No drift check here — drag-html panels live in screen space and can move
    // large SVG-coordinate distances at normal scroll-mode zoom levels, so a
    // fixed SVG-unit maxDrift would reject legitimate saved positions.
    if (uid) {
      const savedPos = getSavedDragPosition(uid);
      if (savedPos) {
        el.setAttribute("transform", `translate(${savedPos.x}, ${savedPos.y})`);
      }
    }

    // Create overlay div - lower z-index so click overlays are on top
    const overlay = document.createElement("div");
    overlay.className = "oscilla-drag-overlay";
    overlay.style.cssText = `
      position: absolute;
      background: transparent;
      cursor: grab;
      z-index: 100001;
      touch-action: none;
    `;

    overlay.title = "Drag to move";

    containerEl.appendChild(overlay);

    // Track drag state
    let isDragging = false;
    let startX = 0, startY = 0;
    let elStartX = 0, elStartY = 0;

    // Get current translate from element
    const getTranslate = () => {
      const transform = el.getAttribute("transform") || "";
      const match = transform.match(/translate\(\s*([-\d.]+)[,\s]+([-\d.]+)\s*\)/);
      if (match) {
        return { x: parseFloat(match[1]), y: parseFloat(match[2]) };
      }
      return { x: 0, y: 0 };
    };

    // Set translate on element
    const setTranslate = (x, y) => {
      el.setAttribute("transform", `translate(${x}, ${y})`);
    };

    // Position overlay over SVG element
    const place = () => placeOverlayOver(overlay, el, containerEl);

    // Initial placement - use setTimeout to ensure transforms are applied
    setTimeout(() => {
      place();
      setTimeout(place, 200);
    }, 50);

    window.addEventListener("resize", place);
    overlay._place = place;

    // Helper to check if there's a clickable element at a given point that should
    // receive the event instead of the drag overlay (click overlays, hide/show circles, etc.)
    const getClickableElementAt = (x, y) => {
      const originalDisplay = overlay.style.display;
      overlay.style.display = 'none';
      const elementUnderneath = document.elementFromPoint(x, y);
      overlay.style.display = originalDisplay;

      if (elementUnderneath && (
          elementUnderneath.classList.contains('oscilla-click-overlay') ||
          elementUnderneath.classList.contains('oscilla-drag-click-overlay') ||
          elementUnderneath.classList.contains('controlxy-hideshow-circle') ||
          elementUnderneath.classList.contains('o2p-launcher-circle'))) {
        return elementUnderneath;
      }
      // Notation noteheads: the click handler lives on the .notation-note <g>
      // wrapper; elementFromPoint may return its <text> child. Walk up a few
      // levels and forward to the wrapper so notehead clicks fire OSC + flash
      // instead of being swallowed by the drag overlay.
      if (elementUnderneath) {
        let n = elementUnderneath;
        for (let depth = 0; n && depth < 4; depth++, n = n.parentElement) {
          if (n.classList && n.classList.contains('notation-note')) {
            return n;
          }
        }
      }
      return null;
    };

    // Prevent touch events from scrolling the page and stop propagation to score handlers
    overlay.addEventListener('touchstart', (e) => {
      const touch = e.touches[0];
      const clickableEl = getClickableElementAt(touch.clientX, touch.clientY);
      if (clickableEl) {
        // Let the pointer handler deal with dispatching the click
        return;
      }
      e.preventDefault();
      e.stopPropagation();
    }, { passive: false, capture: true });

    overlay.addEventListener('touchmove', (e) => {
      e.preventDefault();
      e.stopPropagation();
    }, { passive: false, capture: true });

    overlay.addEventListener('touchend', (e) => {
      e.stopPropagation();
    }, { passive: false, capture: true });

    // Prevent mouse events from reaching score handlers
    overlay.addEventListener('mousedown', (e) => {
      const clickableEl = getClickableElementAt(e.clientX, e.clientY);
      if (!clickableEl) {
        e.stopPropagation();
      }
    }, { capture: true });

    overlay.addEventListener('mousemove', (e) => {
      e.stopPropagation();
    }, { capture: true });

    overlay.addEventListener('mouseup', (e) => {
      e.stopPropagation();
    }, { capture: true });

    // Audio-gesture cue element hosted on/inside the dragged group.
    // The HTML overlay swallows pointer events, so click/hold/double-click
    // gestures bound on the SVG element (bindAudioCueGestures) never fire.
    // Forward real pointerdown/up with live timing; the gesture binder's own
    // move threshold discards the pointerup when the interaction was a drag.
    const findAudioCueTarget = (x, y) => {
      const host = el.closest?.('[data-cue-clickable]') || el.querySelector?.('[data-cue-clickable]');
      if (!host) return null;
      const r = host.getBoundingClientRect();
      return (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) ? host : null;
    };
    let audioGestureTarget = null;
    const forwardPointer = (type, e) => {
      audioGestureTarget?.dispatchEvent(new PointerEvent(type, {
        bubbles: true, cancelable: true,
        clientX: e.clientX, clientY: e.clientY,
        button: 0, pointerId: e.pointerId,
      }));
    };

    // Pointer handlers
    const onPointerDown = (e) => {
      // Check if there's a clickable element at this position that should handle the event
      const clickableEl = getClickableElementAt(e.clientX, e.clientY);
      if (clickableEl) {
        e.preventDefault();
        e.stopPropagation();
        // Dispatch click event to the element (SVG elements won't receive it naturally through HTML overlay)
        clickableEl.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          clientX: e.clientX,
          clientY: e.clientY
        }));
        return;
      }

      audioGestureTarget = findAudioCueTarget(e.clientX, e.clientY);
      if (audioGestureTarget) forwardPointer('pointerdown', e);

      e.preventDefault();
      e.stopPropagation();

      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;

      const current = getTranslate();
      elStartX = current.x;
      elStartY = current.y;

      overlay.style.cursor = "grabbing";
      try { overlay.setPointerCapture(e.pointerId); } catch { /* pointer already released */ }

      overlay.addEventListener("pointermove", onPointerMove);
      overlay.addEventListener("pointerup", onPointerUp);
      overlay.addEventListener("pointercancel", onPointerUp);
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      // Get SVG scale factor from PARENT's CTM (not element's, which includes its own transform)
      const parent = el.parentElement;
      const ctm = parent?.getScreenCTM?.() || el.ownerSVGElement?.getScreenCTM?.();
      const scaleX = ctm ? ctm.a : 1;
      const scaleY = ctm ? ctm.d : 1;

      // Convert screen delta to SVG coordinates
      const svgDx = dx / scaleX;
      const svgDy = dy / scaleY;

      setTranslate(elStartX + svgDx, elStartY + svgDy);
      place(); // Update overlay position
      // Reposition all child overlays during drag
      repositionAllHitLabels();
      repositionAllClickOverlays();
      repositionAllDragClickOverlays();
      repositionAllButtonOverlays();
    };

    const onPointerUp = (e) => {
      if (audioGestureTarget) {
        forwardPointer(e.type === 'pointercancel' ? 'pointercancel' : 'pointerup', e);
        audioGestureTarget = null;
      }
      overlay.style.cursor = "grab";
      try { overlay.releasePointerCapture(e.pointerId); } catch { /* capture already released */ }

      overlay.removeEventListener("pointermove", onPointerMove);
      overlay.removeEventListener("pointerup", onPointerUp);
      overlay.removeEventListener("pointercancel", onPointerUp);

      if (isDragging) {
        const current = getTranslate();

        // Save position if we have a uid
        if (uid) {
          saveDragPosition(uid, current.x, current.y);
        }

        // Reposition any o2p launchers that may be attached to this group
        repositionAllLaunchers();

        // Reposition any click overlays that may be inside this dragged group
        repositionAllClickOverlays();
      }

      isDragging = false;
    };

    overlay.addEventListener("pointerdown", onPointerDown);

    overlay._svgElement = el;
    overlay._uid = uid; // Store uid on overlay for debugging
    built.push(overlay);
  });

  return built;
}

export function destroyAllDragOverlays() {
  document.querySelectorAll(".oscilla-drag-overlay").forEach(el => {
    if (el._svgElement) {
      el._svgElement._dragOverlayBound = false;
    }
    el.remove();
  });
}

export function repositionAllDragOverlays() {
  document.querySelectorAll(".oscilla-drag-overlay").forEach(overlay => {
    if (overlay._place) {
      overlay._place();
    }
  });
}
// Every HTML overlay placed over an SVG element: cue buttons (both kinds),
// click, drag-click and drag overlays. For score content that moves without
// a window resize (animShared's positionRelativeToSource).
export function repositionAllElementOverlays() {
  for (const sel of [".oscilla-cue-button", ".oscilla-click-overlay", ".oscilla-drag-click-overlay", ".oscilla-drag-overlay"]) {
    document.querySelectorAll(sel).forEach(overlay => overlay._place?.());
  }
}

// Zoom changes the rendered score size without a window resize, so overlays
// placed in pixels must re-measure or they drift away from their SVG elements.
window.addEventListener("oscilla:zoom-changed", () => {
  repositionAllButtonOverlays();
  repositionAllClickOverlays();
  repositionAllDragClickOverlays();
  repositionAllDragOverlays();
});
